import React from 'react';
import { Box, Group, Paper, Stack, Text, Title } from '@mantine/core';
import {
  IconArrowBackUp,
  IconBinoculars,
  IconDeviceFloppy,
  IconZoom,
} from '@tabler/icons-react';
import { GanttChartLoader } from './GanttChartLoader';

export type GanttLoaderMark = 'discard' | 'save' | 'zoom' | 'look-ahead';

const MARK_ICONS = {
  discard: IconArrowBackUp,
  save: IconDeviceFloppy,
  zoom: IconZoom,
  'look-ahead': IconBinoculars,
} as const;

export interface GanttBusyOverlayProps {
  visible: boolean;
  title: string;
  message: string;
  mark?: GanttLoaderMark | null;
}

/**
 * Full-area overlay for Gantt load/save/discard/invalidate flows.
 * Keeps the chart visible underneath with blur while showing a readable status card.
 */
export function GanttBusyOverlay({ visible, title, message, mark = null }: GanttBusyOverlayProps) {
  if (!visible) {
    return null;
  }

  const MarkIcon = mark ? MARK_ICONS[mark] : null;

  return (
    <Box
      role="status"
      aria-live="polite"
      aria-busy="true"
      style={{
        position: 'absolute',
        inset: 0,
        zIndex: 250,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        backgroundColor: 'rgba(0, 0, 0, 0.28)',
      }}
    >
      <Paper
        shadow="lg"
        radius="md"
        p="xl"
        withBorder
        style={{
          maxWidth: 440,
          width: '100%',
          textAlign: 'center',
          backgroundColor: 'var(--mantine-color-body)',
        }}
      >
        <Stack align="center" gap="md">
          <GanttChartLoader size="md" />
          <Group gap={8} justify="center" wrap="nowrap">
            {MarkIcon ? <MarkIcon size={18} /> : null}
            <Title order={4} fw={600}>
              {title}
            </Title>
          </Group>
          <Text size="sm" c="dimmed" style={{ lineHeight: 1.5, minHeight: '3em' }}>
            {message}
          </Text>
        </Stack>
      </Paper>
    </Box>
  );
}

export function resolveGanttBusyOverlayState(args: {
  saving: boolean;
  discarding: boolean;
  storeLoading: boolean;
  loadingProgress: string;
  projectDisplayName: string | null;
  hasScheduleData: boolean;
  localProcessing?: { title: string; message: string } | null;
  recalculating?: boolean;
}): { visible: boolean; title: string; message: string; mark: GanttLoaderMark | null } {
  const {
    saving,
    discarding,
    storeLoading,
    loadingProgress,
    projectDisplayName,
    hasScheduleData,
    localProcessing,
    recalculating,
  } = args;

  if (saving) {
    return {
      visible: true,
      title: 'Saving to server',
      message: loadingProgress || 'Sending changes and refreshing schedule...',
      mark: 'save',
    };
  }

  if (discarding) {
    return {
      visible: true,
      title: 'Discarding changes',
      message: loadingProgress || 'Reloading schedule from the server...',
      mark: 'discard',
    };
  }

  if (localProcessing) {
    return {
      visible: true,
      title: localProcessing.title,
      message: localProcessing.message,
      mark: null,
    };
  }

  if (recalculating) {
    return {
      visible: true,
      title: 'Recalculating schedule',
      message: 'Updating dependent tasks. Large projects can take a few seconds.',
      mark: null,
    };
  }

  if (storeLoading || loadingProgress) {
    const projectLabel = projectDisplayName?.trim() || 'this project';
    return {
      visible: true,
      title: hasScheduleData ? 'Updating schedule' : `Preparing Gantt chart for ${projectLabel}`,
      message: loadingProgress || 'Please wait...',
      mark: null,
    };
  }

  return { visible: false, title: '', message: '', mark: null };
}
